import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePageTitle from 'hooks/usePageTitle';
import useDecode from 'hooks/useDecode';
import useCreateGeneralCommunication from 'services/communications/useCreateGeneralCommunication';
import Icon from 'components/blocks/Icon/Icon';
import './DeclaracionAccesibilidad.scss';

const DeclaracionAccesibilidad: React.FC = () => {
  usePageTitle('Declaración de Accesibilidad');

  const navigate = useNavigate();
  const tokenResponse = useDecode('token');
  const userId = tokenResponse?.extension_id;
  const externalUserId = tokenResponse?.extension_external_id;
  const fullName = [tokenResponse?.extension_first_name, tokenResponse?.extension_first_last_name, tokenResponse?.extension_second_last_name].filter(Boolean).join(' ');
  const { createGeneralCommunication, loadingCreateGeneralCommunication } = useCreateGeneralCommunication();

  const [nombre, setNombre] = useState(fullName || '');
  const [asunto, setAsunto] = useState('');
  const [comentario, setComentario] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmitFeedback = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim() || !asunto.trim() || !comentario.trim()) return;

    const payload = {
      asunto: `[Accesibilidad] ${asunto}`,
      mensaje: `Nombre: ${nombre}\n\n${comentario}`,
      userId,
      externalUserId,
      prestacionId: null,
      files: [],
      comunicacionReferenciaId: null,
    };

    createGeneralCommunication(userId, payload, () => {
      setSent(true);
      setNombre('');
      setAsunto('');
      setComentario('');
    });
  };

  return (
    <main id="main-content" className="declaracion-accesibilidad" lang="es">

      <div className="container">
        <div className="btns-header-container da-back">
          <button className="btn btn--ghost btn--sm" onClick={() => navigate(-1)}>
            <Icon name="arrow-left" size={16} /> Volver
          </button>
        </div>

        {/* ── Cabecera ── */}
        <header className="da-header">
          <div className="da-badge" aria-hidden="true">RD 1112/2018</div>
          <h1 className="da-title">Declaración de Accesibilidad</h1>
          <p className="da-subtitle">
            Portal de Pacientes de <strong>SOLIMAT Mutua Colaboradora con la Seguridad Social n.º 72</strong>
          </p>
        </header>

        {/* ── Estado de conformidad ── */}
        <section className="da-section" aria-labelledby="estado-heading">
          <h2 id="estado-heading" className="da-section__title">
            <span className="da-section__num" aria-hidden="true">01</span>
            Estado de conformidad
          </h2>
          <div className="da-conformance-card da-conformance-card--partial" role="status">
            <span className="da-conformance-card__icon" aria-hidden="true">◑</span>
            <div>
              <strong className="da-conformance-card__label">Parcialmente conforme</strong>
              <p className="da-conformance-card__text">
                Este portal cumple parcialmente con los requisitos de la{' '}
                <abbr title="Norma EN 301 549 V3.2.1">EN 301 549 V3.2.1</abbr> y las{' '}
                <abbr title="Web Content Accessibility Guidelines">WCAG</abbr> 2.1, nivel AA,
                debido a las excepciones e incumplimientos indicados en esta declaración.
              </p>
            </div>
          </div>
          <p className="da-text">
            Esta declaración se aplica al Portal de Pacientes de SOLIMAT, accesible en{' '}
            <strong>pacientes.solimat.es</strong>, gestionado por SOLIMAT Mutua Colaboradora
            con la Seguridad Social n.º 72. La declaración fue preparada conforme al procedimiento
            establecido en el Real Decreto 1112/2018, de 7 de septiembre, sobre accesibilidad de
            los sitios web y aplicaciones para dispositivos móviles del sector público.
          </p>
        </section>

        {/* ── Contenido no accesible ── */}
        <section className="da-section" aria-labelledby="contenido-heading">
          <h2 id="contenido-heading" className="da-section__title">
            <span className="da-section__num" aria-hidden="true">02</span>
            Contenido no accesible
          </h2>
          <p className="da-text">
            El contenido que se recoge a continuación no es accesible por los siguientes motivos:
          </p>

          <h3 className="da-subsection__title">Incumplimientos de la norma</h3>
          <ul className="da-list" role="list">
            <li>
              Los documentos PDF generados automáticamente desde las secciones{' '}
              <em>Historial clínico</em>, <em>Mis gestiones</em> y <em>Notificaciones</em>{' '}
              (partes de alta/baja, resúmenes de prestaciones y documentos de pago directo) no
              disponen de etiquetado semántico completo ni texto alternativo en las tablas de datos
              (criterio WCAG 1.3.1 — Información y relaciones). Se está trabajando en la
              regeneración de estos documentos con la librería accesible correspondiente.
            </li>
            <li>
              El visor de localización de centros asistenciales de la sección{' '}
              <em>Red de centros</em> (<code>/red-de-centros</code>), integrado mediante la API
              de Google Maps, presenta elementos de control sin etiqueta suficiente y ratios de
              contraste inferiores a 4,5:1 en algunos estados de botón (criterios WCAG 1.4.3 y
              4.1.2). Este componente es un servicio de tercero sobre el que SOLIMAT tiene
              capacidad de modificación limitada.
            </li>
            <li>
              En los formularios complejos de gestiones basados en componentes Syncfusion
              (sección <code>/mis-gestiones</code>), determinados mensajes de validación no
              están todavía asociados programáticamente al campo que los origina mediante{' '}
              <code>aria-describedby</code> (criterio WCAG 3.3.1 — Identificación de errores).
              El resto de formularios del portal han sido corregidos en el ciclo de revisión
              de junio de 2026. La corrección de los formularios Syncfusion está planificada
              para el próximo ciclo de desarrollo.
            </li>
          </ul>

          <h3 className="da-subsection__title">Carga desproporcionada</h3>
          <p className="da-text">
            No aplica. No se ha invocado la excepción de carga desproporcionada en ninguna parte
            del portal en este momento.
          </p>

          <h3 className="da-subsection__title">Contenido excluido del ámbito de aplicación</h3>
          <p className="da-text">
            Los archivos de ofimática (PDF) publicados antes del 23 de septiembre de 2018 están
            excluidos conforme al artículo 3.2 del RD 1112/2018.
          </p>
        </section>

        {/* ── Preparación de la declaración ── */}
        <section className="da-section" aria-labelledby="preparacion-heading">
          <h2 id="preparacion-heading" className="da-section__title">
            <span className="da-section__num" aria-hidden="true">03</span>
            Preparación de esta declaración
          </h2>
          <dl className="da-definition-list">
            <div className="da-definition-list__item">
              <dt>Fecha de la última revisión</dt>
              <dd><time dateTime="2026-06">Junio de 2026</time></dd>
            </div>
            <div className="da-definition-list__item">
              <dt>Método empleado</dt>
              <dd>
                Autoevaluación realizada por el equipo de desarrollo de SOLIMAT, apoyada en
                herramientas automáticas (axe DevTools, Chrome Accessibility DevTools, Lighthouse)
                y revisión manual conforme a las{' '}
                <abbr title="Web Content Accessibility Guidelines">WCAG</abbr> 2.1 nivel AA.
              </dd>
            </div>
            <div className="da-definition-list__item">
              <dt>Componentes evaluados en el ciclo junio 2026</dt>
              <dd>
                Layout global, footer, cabecera, modales, acordeones, formularios de contraseña
                y gestiones, botones de archivos adjuntos, tarjetas de cita, historial asistencial,
                tabla de encuestas, página de inicio y formulario de Real Decreto.
              </dd>
            </div>
            <div className="da-definition-list__item">
              <dt>Tecnologías de apoyo utilizadas en la evaluación</dt>
              <dd>NVDA 2023, VoiceOver (iOS 17), teclado exclusivo, Chrome Accessibility DevTools.</dd>
            </div>
            <div className="da-definition-list__item">
              <dt>Tecnologías en las que se basa la accesibilidad del contenido</dt>
              <dd>HTML5, CSS3, WAI-ARIA 1.2, JavaScript (React 19).</dd>
            </div>
          </dl>
        </section>

        {/* ── Mecanismo de contacto y retroalimentación ── */}
        <section className="da-section da-section--highlight" aria-labelledby="feedback-heading">
          <h2 id="feedback-heading" className="da-section__title">
            <span className="da-section__num" aria-hidden="true">04</span>
            Mecanismo de contacto y retroalimentación
          </h2>
          <p className="da-text">
            Si ha experimentado alguna barrera de accesibilidad o necesita información presentada
            de una manera más accesible, puede comunicárnoslo a través de los siguientes medios.
            Nos comprometemos a responder en un plazo máximo de <strong>15 días hábiles</strong>,
            conforme al artículo 10.2 del RD 1112/2018.
          </p>

          {/* Formulario de contacto */}
          {sent ? (
            <div className="da-feedback-success" role="status" aria-live="polite">
              <p><strong>¡Gracias!</strong> Tu mensaje ha sido enviado correctamente. Te responderemos en un plazo máximo de 15 días hábiles.</p>
              <button className="da-feedback-form__btn" type="button" onClick={() => setSent(false)}>
                Enviar otro mensaje
              </button>
            </div>
          ) : (
            <form
              className="da-feedback-form"
              onSubmit={handleSubmitFeedback}
              noValidate
              aria-labelledby="feedback-heading"
            >
              <fieldset className="da-feedback-form__fieldset">
                <legend className="sr-only">Formulario de contacto sobre accesibilidad</legend>
                <div className="da-feedback-form__field">
                  <label htmlFor="da-nombre">Nombre</label>
                  <input
                    id="da-nombre"
                    type="text"
                    value={nombre}
                    readOnly
                    aria-readonly="true"
                    autoComplete="name"
                  />
                </div>
                <div className="da-feedback-form__field">
                  <label htmlFor="da-asunto">Asunto <span aria-hidden="true">*</span></label>
                  <input
                    id="da-asunto"
                    type="text"
                    value={asunto}
                    onChange={(e) => setAsunto(e.target.value)}
                    required
                    aria-required="true"
                    placeholder="Ej: Problema de contraste en la sección…"
                  />
                </div>
                <div className="da-feedback-form__field">
                  <label htmlFor="da-comentario">Comentario <span aria-hidden="true">*</span></label>
                  <textarea
                    id="da-comentario"
                    rows={5}
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    required
                    aria-required="true"
                    placeholder="Describa la barrera de accesibilidad encontrada…"
                  />
                </div>
              </fieldset>
              <button
                className="da-feedback-form__btn"
                type="submit"
                disabled={loadingCreateGeneralCommunication || !asunto.trim() || !comentario.trim()}
                aria-disabled={loadingCreateGeneralCommunication || !asunto.trim() || !comentario.trim()}
              >
                {loadingCreateGeneralCommunication ? 'Enviando…' : 'Enviar'}
              </button>
            </form>
          )}

        </section>

        {/* ── Procedimiento de aplicación ── */}
{/* ── Procedimiento de reclamación ── */}
        <section className="da-section" aria-labelledby="procedimiento-heading">
          <h2 id="procedimiento-heading" className="da-section__title">
            <span className="da-section__num" aria-hidden="true">05</span>
            Procedimiento de reclamación
          </h2>
          <p className="da-text">
            Si no recibe respuesta en el plazo indicado, o si considera que la respuesta recibida
            no satisface su solicitud, puede presentar una reclamación conforme al procedimiento
            previsto en el <strong>artículo 13 del RD 1112/2018</strong>, con el fin de conocer y
            oponerse a los motivos de la desestimación, instar la adopción de las medidas oportunas
            o exponer las razones por las que considera que la respuesta no cumple los requisitos
            exigidos.
          </p>
          <p className="da-text">
            La reclamación puede presentarse a través de los siguientes canales, así como por el
            resto de opciones recogidas en la Ley 39/2015, de 1 de octubre, del Procedimiento
            Administrativo Común de las Administraciones Públicas:
          </p>
          <ul className="da-list" role="list">
            <li>
              <a
                href="https://www.inclusion.gob.es/accesibilidad"
                className="da-external-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Procedimiento de reclamación de accesibilidad del Ministerio de Inclusión, Seguridad Social y Migraciones (abre en nueva ventana)"
              >
                Procedimiento de reclamación — Ministerio de Inclusión, Seguridad Social y
                Migraciones
                <span className="da-external-link__icon" aria-hidden="true"> ↗</span>
              </a>
            </li>
            <li>
              <a
                href="https://rec.redsara.es/registro/action/are/acceso.do"
                className="da-external-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Registro Electrónico General de la Administración General del Estado (abre en nueva ventana)"
              >
                Registro Electrónico General de la Administración General del Estado
                <span className="da-external-link__icon" aria-hidden="true"> ↗</span>
              </a>
            </li>
          </ul>
          <div className="da-notice" role="note">
            <span className="da-notice__icon" aria-hidden="true">ℹ</span>
            <p>
              Esta declaración fue elaborada el <time dateTime="2026-06-01">1 de junio de 2026</time> y
              será revisada anualmente o cuando se produzcan cambios significativos en el portal.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default DeclaracionAccesibilidad;
